<template>
  <div class="date-range-filter">
    <b-dropdown
      class="preset-dropdown"
      :text="activePreset"
      size="sm"
      variant="link"
      right
    >
      <b-dropdown-item
        v-for="preset in presets"
        :key="preset"
        :active="presetSelected === preset"
        @click="onSelectPreset(preset)"
      >
        {{ preset }}
      </b-dropdown-item>
    </b-dropdown>

    <div class="form-control d-flex align-items-center p-0 mr-1">
      <input
        :id="`from_${_uid}`"
        v-model="formattedFrom"
        type="text"
        readonly
        class="form-control text-center bg-transparent border-0 padding-0"
      >
      <span>&mdash;</span>
      <input
        :id="`to_${_uid}`"
        v-model="formattedTo"
        type="text"
        readonly
        class="form-control text-center bg-transparent border-0 padding-0"
      >
    </div>

    <b-popover
      class="popover-reactive"
      :target="`from_${_uid}`"
      triggers="focus"
      placement="bottom"
    >
      <datepicker
        v-model="from"
        inline
        name="from"
        class="border-0 mb-1"
        :highlighted="highlighted"
        :disabled-dates="fromDisabledDates"
        @selected="onSelectFrom"
      ></datepicker>
    </b-popover>
    <b-popover
      class="popover-reactive"
      :target="`to_${_uid}`"
      triggers="focus"
      placement="bottom"
    >
      <datepicker
        v-model="to"
        inline
        name="to"
        class="border-0 mb-1"
        :highlighted="highlighted"
        :disabled-dates="toDisabledDates"
        @selected="onSelectTo"
      ></datepicker>
    </b-popover>
  </div>
</template>

<style lang="scss" scoped>
.date-range-filter {
  .preset-dropdown {
    position: absolute;
    right: 0.50rem;
    top: -0.25rem;
  }
}
</style>

<script>
import Datepicker from 'vuejs-datepicker'
import get from 'lodash/get'
import findKey from 'lodash/findKey'

import {
  PRESET_RANGES,
  formatDate,
  isSame
} from '@/lib/moment'

export default {
  name: 'DateRange',
  components: {
    Datepicker
  },

  props: {
    data: {
      type: Object
    },

    value: {
      type: Object,
      default () {
        return get(this.data, 'default') || {
          from: null,
          to: null
        }
      }
    }
  },

  data () {
    return {
      from: null,
      to: null
    }
  },

  computed: {
    targetId () {
      return `popover-target-${this._uid}`
    },

    activePreset () {
      return this.presetSelected || 'Custom'
    },

    dateFormat () {
      return get(this.data, 'format', 'MMM Do YYYY')
    },

    formattedTo () {
      if (!this.to) {
        return ''
      }
      return formatDate(this.to, this.dateFormat)
    },

    formattedFrom () {
      if (!this.from) {
        return ''
      }
      return formatDate(this.from, this.dateFormat)
    },

    formattedValue () {
      if (!this.from && !this.to) {
        return ''
      }
      const from = formatDate(this.from, this.dateFormat)
      const to = formatDate(this.to, this.dateFormat)
      return `${from} - ${to}`
    },

    highlighted () {
      return {
        from: this.from,
        to: this.to
      }
    },

    fromDisabledDates () {
      return this.to ? {
        from: this.to
      } : {}
    },

    toDisabledDates () {
      return this.from ? {
        to: this.from
      } : {}
    },

    presets () {
      return Object.keys(PRESET_RANGES)
    },

    presetSelected () {
      return findKey(PRESET_RANGES, range =>
        isSame(range[0], this.from, 'day') &&
        isSame(range[1], this.to, 'day')
      )
    }
  },

  watch: {
    value: {
      handler () {
        this.from = moment(this.value.from).startOf('day').toDate()
        this.to = moment(this.value.to).endOf('day').toDate()
      },
      deep: true
    }
  },

  mounted () {
    this.from = moment(this.value.from).startOf('day').toDate()
    this.to = moment(this.value.to).endOf('day').toDate()
  },

  methods: {
    updateValue (from, to) {
      this.$emit('input', {
        from: moment(from).format(),
        to: moment(to).format()
      })
    },

    onSelectFrom (date) {
      this.from = date
      this.updateValue(this.from, this.to)
    },

    onSelectTo (date) {
      this.to = date
      this.updateValue(this.from, this.to)
    },

    onSelectPreset (preset) {
      const range = PRESET_RANGES[preset]
      this.updateValue(...range)
    }
  }
}
</script>
